import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, CheckCircle2, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DIRECTORY_CATEGORIES = [
  'Nonprofit / NGO',
  'Research Lab / Academic',
  'Government / Regulatory',
  'Industry / Company',
  'Tool / Technology Provider',
  'Advocacy / Community Group',
];

const initialState = {
  org_name: '',
  website: '',
  description: '',
  category: '',
  region: '',
  country: '',
  logo_url: '',
  contact_email: '',
  submitter_name: '',
  submitter_email: '',
};

const DirectorySubmitPage = () => {
  const [formData, setFormData] = useState(initialState);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Logo must be under 8 MB.');
      return;
    }
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Logo must be JPEG, PNG, or WebP.');
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/onboarding/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url) {
        const absoluteUrl = res.data.url.startsWith('http')
          ? res.data.url
          : `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
        setFormData((prev) => ({ ...prev, logo_url: absoluteUrl }));
        setLogoPreview(absoluteUrl);
        toast.success('Logo uploaded.');
      } else {
        toast.error('Upload failed.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category) {
      toast.error('Please select a category.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/directory/submit`, formData);
      if (res.data?.success) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error('Submission failed. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-slate-50 min-h-screen" data-testid="directory-submit-success">
        <section className="bg-slate-900 text-white py-20 md:py-28">
          <div className="max-w-3xl mx-auto container-padding text-center">
            <CheckCircle2 className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
            <p className="caption text-cyan-400 mb-3">Submission Received</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Your listing is under review.
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              All directory submissions are reviewed by Safe Standard before publication.
              We&rsquo;ll contact you if we need more information. A confirmation has been
              sent to your email.
            </p>
          </div>
        </section>
        <section className="py-12 text-center space-x-4">
          <Link to="/directory" className="btn-primary" data-testid="directory-back-link">
            Back to Directory
          </Link>
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4">
            Return home
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="directory-submit-page">
      <section className="bg-slate-900 text-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto container-padding">
          <p className="caption text-cyan-400 mb-4">Safe Standard Directory</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
            Submit a listing.
          </h1>
          <p className="text-base md:text-lg text-slate-300">
            Help map the internet and AI safety ecosystem. All submissions are reviewed by
            Safe Standard before they appear publicly. Inclusion does not constitute
            endorsement, certification, approval, partnership, or formal verification by
            Safe Standard unless expressly stated.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto container-padding">
          <form onSubmit={handleSubmit} className="space-y-12" data-testid="directory-form">
            {/* PUBLIC LISTING */}
            <div className="bg-white border border-slate-200 p-6 md:p-8">
              <p className="caption text-cyan-700 mb-2">Section 1 — Public Listing</p>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Listing details</h2>
              <p className="text-sm text-slate-500 mb-6">These fields appear on the public directory once approved.</p>

              {/* Logo */}
              <div className="mb-6">
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Logo (optional)
                </label>
                <div className="flex items-start gap-4">
                  <div className="w-28 h-28 bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                    {logoPreview ? (
                      <img src={logoPreview} alt="logo preview" className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleLogoSelect} className="hidden" data-testid="dir-logo-input" />
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={logoUploading} className="bg-slate-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-700 disabled:opacity-50" data-testid="dir-logo-btn">
                      <Upload className="w-3 h-3 inline mr-2" />
                      {logoUploading ? 'Uploading…' : logoPreview ? 'Replace logo' : 'Upload logo'}
                    </button>
                    <p className="text-xs text-slate-500 mt-2">JPEG, PNG, or WebP. Maximum 8 MB.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <Field label="Organization or project name" required>
                  <input name="org_name" required value={formData.org_name} onChange={handleChange} className={inputClass} data-testid="dir-org-name" />
                </Field>
                <Field label="Website URL" required>
                  <input name="website" required value={formData.website} onChange={handleChange} className={inputClass} data-testid="dir-website" placeholder="https://..." />
                </Field>
                <Field label="Category" required>
                  <select name="category" required value={formData.category} onChange={handleChange} className={inputClass} data-testid="dir-category">
                    <option value="" disabled>Select a category…</option>
                    {DIRECTORY_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Short description / mission" required hint="One paragraph describing the organization or project.">
                  <textarea name="description" required maxLength={1200} value={formData.description} onChange={handleChange} rows={4} className={inputClass} data-testid="dir-description" />
                </Field>
                <Field label="Region (optional)" hint="e.g. 'Europe', 'Sub-Saharan Africa', 'Global'.">
                  <input name="region" value={formData.region} onChange={handleChange} className={inputClass} data-testid="dir-region" />
                </Field>
                <Field label="Country (optional)">
                  <input name="country" value={formData.country} onChange={handleChange} className={inputClass} data-testid="dir-country" />
                </Field>
              </div>
            </div>

            {/* INTERNAL */}
            <div className="bg-white border border-slate-200 p-6 md:p-8">
              <p className="caption text-slate-500 mb-2">Section 2 — Internal (not shown publicly)</p>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Your contact details</h2>
              <p className="text-sm text-slate-500 mb-6">Used only by Safe Standard to review this submission and follow up if needed. Never displayed publicly.</p>
              <div className="space-y-5">
                <Field label="Your name" required>
                  <input name="submitter_name" required value={formData.submitter_name} onChange={handleChange} className={inputClass} data-testid="dir-submitter-name" />
                </Field>
                <Field label="Your email" required hint="We&rsquo;ll send a confirmation here.">
                  <input type="email" name="submitter_email" required value={formData.submitter_email} onChange={handleChange} className={inputClass} data-testid="dir-submitter-email" />
                </Field>
                <Field label="Organization contact email (optional)">
                  <input type="email" name="contact_email" value={formData.contact_email} onChange={handleChange} className={inputClass} data-testid="dir-contact-email" />
                </Field>
              </div>
            </div>

            <div className="pt-2">
              <button type="submit" disabled={submitting || logoUploading} className={`btn-primary w-full md:w-auto ${(submitting || logoUploading) ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="dir-submit-btn">
                {submitting ? 'Submitting…' : 'Submit for review'}
              </button>
              <p className="text-xs text-slate-500 mt-3">
                By submitting, you confirm the information is accurate. Safe Standard may review,
                edit, decline, archive, or remove listings at its discretion.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

const inputClass = 'w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 transition-colors bg-white';

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

export default DirectorySubmitPage;
